import { useState } from 'react'
import type { FC } from 'react'
import { Box, Button, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import AttachmentIcon from '@mui/icons-material/AttachFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { useDropzone } from 'react-dropzone'
import { acceptMimeTypes } from '~/shared/lib/dropzoneAccept'

import IMG from './img.png'
import { red } from '@mui/material/colors'
import { visuallyHidden } from '@mui/utils'
import { FFModal } from '~/shared/ui'
// Separate types to distinguish between endpoints upstream
export interface FileWithType {
  data: File
  type: 'image' | 'document'
}

interface FileUploadProps {
  maxFiles: number
  files?: FileWithType[]
  supportedImageTypes: Array<string>
  supportedDocTypes: Array<string>
  supportMessage: string
  handleFile: (files: FileWithType[]) => void
  handleClear: () => void
}

const maxFileNameLength = 63

const EmailFileUpload: FC<FileUploadProps> = props => {
  const [showModal, setShowModal] = useState(false)
  const [files, setFiles] = useState<FileWithType[] | null>(null)
  const { getRootProps, fileRejections, isFocused, isDragAccept, isDragReject, getInputProps } =
    useDropzone({
      accept: acceptMimeTypes(props.supportedImageTypes.concat(props.supportedDocTypes)),
      maxFiles: props.maxFiles,
      maxSize: 1e6 * 100, // 100MB upload max
      multiple: true,
      validator: file => {
        if (file.name.length > maxFileNameLength) {
          const filtered_files = files?.filter(x => x.data.name != file.name)
          setFiles(filtered_files ?? null)
          return {
            code: 'name-too-large',
            message: `Ensure the filename has no more than ${maxFileNameLength} characters`,
          }
        }
        if (files && files?.length > props.maxFiles) {
          return {
            code: 'files-too-many',
            message: `Maximum allowed file is ${props.maxFiles}`,
          }
        }
        return null
      },
      onDropAccepted: accepted => {
        const [dropped] = accepted
        if (!dropped) return
        const droppedFile: FileWithType = {
          data: dropped,
          type: props.supportedImageTypes.includes(dropped.type) ? 'image' : 'document',
        }
        setFiles(props.maxFiles === 1 ? [droppedFile] : [...(files ?? []), droppedFile])
      },
    })
  return (
    <>
      <FFModal
        open={showModal}
        footer={
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={props.handleClear} color="secondary">
              Cancel
            </Button>
            <Box sx={{ ml: 1 }}>
              <Button
                disabled={!files || files?.length === 0}
                variant="contained"
                color="primary"
                onClick={() => {
                  if (files) props.handleFile(files)
                  setShowModal(false)
                }}
              >
                Add
              </Button>
            </Box>
          </Box>
        }
      >
        <div {...getRootProps()}>
          <Box sx={{ p: 5 }}>
            <Box
              sx={{
                border: '0.3rem dashed',
                borderRadius: '4px',
                borderColor: isDragReject
                  ? red[500]
                  : `grey.${isFocused || isDragAccept ? 700 : 400}`,
                py: 10,
                px: 5,
              }}
            >
              <Typography align="center">
                <img width={220} src={IMG} alt="Upload file" />
                <br />
                Drop your file here or{' '}
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  browse
                </Typography>
                <br />
                {props.supportMessage}
              </Typography>
            </Box>
            {files?.length
              ? files.map((v, i) => (
                  <Box key={i} sx={{ mt: 2, bgcolor: 'grey.100', borderRadius: '5px', p: 1 }}>
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                      <InsertDriveFileIcon color="secondary" />
                      <Box sx={{ ml: 1, overflow: 'hidden' }}>
                        <Typography style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {v.data.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              : null}
            {fileRejections.length ? (
              <Box sx={{ mt: 2, bgcolor: 'grey.100', borderRadius: '5px', p: 1 }}>
                {fileRejections.map(fileRejection => (
                  <Box key={fileRejection.file.name} sx={{ ml: 1, overflow: 'hidden' }}>
                    {fileRejection.errors.map(e => (
                      <Typography
                        key={e.code}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: red[500] }}
                      >
                        {`${fileRejection.file.name} ${e.message}`}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </div>
      </FFModal>
      <div style={visuallyHidden}>
        <Typography>
          <input {...getInputProps()} />
        </Typography>
      </div>
      {/* If no files or length of files is less than 4, user can attach upto 3 files */}
      {(!files || (files && files?.length < 4)) && (
        <IconButton onClick={() => setShowModal(true)} size="large">
          <AttachmentIcon />
        </IconButton>
      )}
    </>
  )
}

export default EmailFileUpload
